import * as React from 'react';

import {
  SalesItemLineType,
  SalesLoadLineItemPhase,
} from '@rematter/frontend-lib-api-client';
import { useToast, uuid } from '@rematter/ui-web';
import { useSetAtom } from 'jotai';

import { RemovedSalesOrderLineItem } from '../_components/_shared/LineItems/types';
import { $loadForm } from '../_state/loadForm.state';
import { $materialCellFocusSignal } from '../_state/loadsTab.state';
import { useTranslations } from '../i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
  LoadFormState,
} from '../types/load';

type UpdateContext = {
  id: string;
  currentItem: LoadFormLineItem;
  updates: Partial<LoadFormLineItem>;
  prevForm: LoadFormState;
  isMaterialSelection: boolean;
  isTypeChange: boolean;
  isSalesOrderItem: boolean;
  existingWithSameItemId: LoadFormLineItem[];
  salesOrderLineItems: LoadFormLineItem[];
  removedSalesOrderLineItems: RemovedSalesOrderLineItem[] | undefined;
};

type ValidationResult =
  | { type: 'error' }
  | { type: 'early-return'; lineItems: LoadFormLineItem[] }
  | { type: 'continue' };

const findItemsWithSameItemId = (
  lineItems: LoadFormLineItem[],
  excludeId: string,
  targetItemId: string,
): LoadFormLineItem[] =>
  lineItems.filter(
    (item) => item.id !== excludeId && item.itemId === targetItemId,
  );

const hasBothItemTypes = (items: LoadFormLineItem[]): boolean =>
  items.some((i) => i.type === 'Raw') && items.some((i) => i.type === 'Tagged');

const tryRestoreRemovedSalesOrderItem = (
  currentItem: LoadFormLineItem,
  targetItemId: string,
  removedItems: RemovedSalesOrderLineItem[] | undefined,
): Partial<LoadFormLineItem> | null => {
  if (!currentItem.pendingSalesOrderMaterialSelection || !removedItems) {
    return null;
  }

  const removedItem = removedItems.find((item) => item.itemId === targetItemId);
  if (!removedItem) {
    return null;
  }

  return {
    itemId: targetItemId,
    unitPrice: removedItem.unitPrice,
    pricingUnit: removedItem.pricingUnit,
    weights: {
      ...currentItem.weights,
      [enumLoadWeightType.QuantityOwed]: removedItem.quantityOwedWeight,
    },
    pendingSalesOrderMaterialSelection: false,
  };
};

const resolveTypeForDuplicateAvoidance = (
  currentType: SalesItemLineType | null | undefined,
  existingItems: LoadFormLineItem[],
): SalesItemLineType | null | undefined => {
  if (!currentType) {
    return currentType;
  }
  const hasMatchingType = existingItems.some((i) => i.type === currentType);
  if (!hasMatchingType) {
    return currentType;
  }
  return currentType === 'Raw' ? 'Tagged' : 'Raw';
};

const applySalesOrderUnitPrice = (
  updatedLine: LoadFormLineItem,
  salesOrderLineItems: LoadFormLineItem[],
  hasSalesOrder: boolean,
  itemIdChanged: boolean,
): LoadFormLineItem => {
  if (!itemIdChanged || !hasSalesOrder || !updatedLine.itemId) {
    return updatedLine;
  }

  if (salesOrderLineItems.length === 0) {
    return updatedLine;
  }

  const correspondingItem = salesOrderLineItems.find(
    (salesItem) => salesItem.itemId === updatedLine.itemId,
  );

  if (!correspondingItem) {
    return updatedLine;
  }

  return {
    ...updatedLine,
    unitPrice: correspondingItem.unitPrice ?? null,
  };
};

const buildUpdateContext = (
  id: string,
  currentItem: LoadFormLineItem,
  updates: Partial<LoadFormLineItem>,
  prevForm: LoadFormState,
  salesOrderLineItems: LoadFormLineItem[],
  removedSalesOrderLineItems: RemovedSalesOrderLineItem[] | undefined,
): UpdateContext => {
  const isMaterialSelection = updates.itemId !== undefined;
  const isTypeChange =
    updates.type !== undefined && updates.type !== currentItem.type;
  const isSalesOrderItem = Boolean(
    currentItem.includeInSalesOrder ||
    currentItem.pendingSalesOrderMaterialSelection,
  );

  const targetItemId = updates.itemId ?? currentItem.itemId;
  const needsDuplicateCheck =
    (isMaterialSelection || isTypeChange) && targetItemId;

  const existingWithSameItemId = needsDuplicateCheck
    ? findItemsWithSameItemId(prevForm.lineItems, id, targetItemId)
    : [];

  return {
    id,
    currentItem,
    updates,
    prevForm,
    isMaterialSelection,
    isTypeChange,
    isSalesOrderItem,
    existingWithSameItemId,
    salesOrderLineItems,
    removedSalesOrderLineItems,
  };
};

const validateDuplicates = (ctx: UpdateContext): ValidationResult => {
  const isRelevantUpdate = ctx.isMaterialSelection || ctx.isTypeChange;
  if (!isRelevantUpdate || ctx.existingWithSameItemId.length === 0) {
    return { type: 'continue' };
  }

  if (ctx.isSalesOrderItem && ctx.isMaterialSelection) {
    return { type: 'error' };
  }

  if (hasBothItemTypes(ctx.existingWithSameItemId)) {
    return { type: 'error' };
  }

  const targetType = ctx.updates.type ?? ctx.currentItem.type;
  const hasConflictingType = ctx.existingWithSameItemId.some(
    (item) => item.type === targetType,
  );
  if (hasConflictingType && ctx.isTypeChange) {
    return { type: 'error' };
  }

  return { type: 'continue' };
};

const tryRestoreRemovedItem = (ctx: UpdateContext): ValidationResult => {
  if (!ctx.isMaterialSelection || !ctx.updates.itemId) {
    return { type: 'continue' };
  }

  const restoredUpdates = tryRestoreRemovedSalesOrderItem(
    ctx.currentItem,
    ctx.updates.itemId,
    ctx.removedSalesOrderLineItems,
  );

  if (!restoredUpdates) {
    return { type: 'continue' };
  }

  const lineItems = ctx.prevForm.lineItems.map((line) =>
    line.id === ctx.id ? { ...line, ...restoredUpdates } : line,
  );

  return { type: 'early-return', lineItems };
};

const computeFinalUpdates = (ctx: UpdateContext): Partial<LoadFormLineItem> => {
  if (ctx.isSalesOrderItem) {
    return ctx.updates;
  }

  const resolvedType = resolveTypeForDuplicateAvoidance(
    ctx.currentItem.type,
    ctx.existingWithSameItemId,
  );

  if (resolvedType === ctx.currentItem.type) {
    return ctx.updates;
  }

  return { ...ctx.updates, type: resolvedType };
};

const applyUpdatesToLineItems = (
  ctx: UpdateContext,
  finalUpdates: Partial<LoadFormLineItem>,
): LoadFormLineItem[] =>
  ctx.prevForm.lineItems.map((line) => {
    if (line.id !== ctx.id) {
      return line;
    }

    const updatedLine = { ...line, ...finalUpdates };
    const itemIdChanged =
      ctx.updates.itemId !== undefined && ctx.updates.itemId !== line.itemId;

    return applySalesOrderUnitPrice(
      updatedLine,
      ctx.salesOrderLineItems,
      Boolean(ctx.prevForm.salesOrderId),
      itemIdChanged,
    );
  });

type UseLoadLineItemsManagerOptions = {
  salesOrderLineItems: LoadFormLineItem[];
  phase?: SalesLoadLineItemPhase;
  removedSalesOrderLineItems?: RemovedSalesOrderLineItem[];
  skipDuplicateValidation?: boolean;
};

export const useLoadLineItemsManager = ({
  salesOrderLineItems,
  phase,
  removedSalesOrderLineItems,
  skipDuplicateValidation,
}: UseLoadLineItemsManagerOptions) => {
  const t = useTranslations();
  const toast = useToast();
  const updateForm = useSetAtom($loadForm);
  const setMaterialCellFocusSignal = useSetAtom($materialCellFocusSignal);

  const salesOrderLineItemsRef = React.useRef(salesOrderLineItems);
  const removedSalesOrderLineItemsRef = React.useRef(
    removedSalesOrderLineItems,
  );

  React.useEffect(() => {
    salesOrderLineItemsRef.current = salesOrderLineItems;
  }, [salesOrderLineItems]);

  React.useEffect(() => {
    removedSalesOrderLineItemsRef.current = removedSalesOrderLineItems;
  }, [removedSalesOrderLineItems]);

  const updateItem = React.useCallback(
    (id: string, updates: Partial<LoadFormLineItem>): void => {
      updateForm((prevForm) => {
        const currentItem = prevForm.lineItems.find((li) => li.id === id);
        if (!currentItem) {
          return prevForm;
        }

        const ctx = buildUpdateContext(
          id,
          currentItem,
          updates,
          prevForm,
          salesOrderLineItemsRef.current,
          removedSalesOrderLineItemsRef.current,
        );

        if (!skipDuplicateValidation) {
          const validation = validateDuplicates(ctx);
          if (validation.type === 'error') {
            toast.error(t.loadsView.messages.duplicateRawItem);
            return prevForm;
          }
        }

        const restoration = tryRestoreRemovedItem(ctx);
        if (restoration.type === 'early-return') {
          return { ...prevForm, lineItems: restoration.lineItems };
        }

        const finalUpdates = skipDuplicateValidation
          ? ctx.updates
          : computeFinalUpdates(ctx);
        const lineItems = applyUpdatesToLineItems(ctx, finalUpdates);

        return { ...prevForm, lineItems };
      });
    },
    [t, toast, updateForm],
  );

  const addItem = React.useCallback(
    (type: SalesItemLineType, data?: Partial<LoadFormLineItem>) => {
      const newItemId = `temp-${uuid()}`;
      updateForm((prevForm) => {
        const lineItems = [...prevForm.lineItems];
        const newItem = {
          type,
          id: newItemId,
          itemId: null,
          tagId: null,
          phase: phase ?? null,
          unitPrice: null,
          pricingUnit: null,
          weights: {
            [enumLoadWeightType.Gross]: null,
            [enumLoadWeightType.Tare]: null,
            [enumLoadWeightType.Net]: null,
          },
          ...data,
        };

        const currentSalesOrderItems = salesOrderLineItemsRef.current;
        if (
          prevForm.salesOrderId &&
          newItem.itemId &&
          currentSalesOrderItems.length > 0
        ) {
          const correspondingSalesOrderItem = currentSalesOrderItems.find(
            (salesItem) => salesItem.itemId === newItem.itemId,
          );
          if (correspondingSalesOrderItem) {
            newItem.unitPrice = correspondingSalesOrderItem.unitPrice ?? null;
          }
        }

        lineItems.push(newItem);
        return { ...prevForm, lineItems };
      });
      setMaterialCellFocusSignal(newItemId);
    },
    [updateForm, phase, setMaterialCellFocusSignal],
  );

  const addSalesOrderItem = React.useCallback(
    (type: SalesItemLineType) => {
      const newItemId = `temp-${uuid()}`;
      const removedItems = removedSalesOrderLineItemsRef.current ?? [];

      updateForm((prevForm) => {
        const lineItems = [...prevForm.lineItems];

        if (removedItems.length === 1) {
          const removedItem = removedItems[0];
          const newItem: LoadFormLineItem = {
            type,
            id: newItemId,
            itemId: removedItem.itemId,
            tagId: null,
            phase: phase ?? null,
            unitPrice: removedItem.unitPrice,
            pricingUnit: removedItem.pricingUnit,
            weights: {
              [enumLoadWeightType.Gross]: null,
              [enumLoadWeightType.Tare]: null,
              [enumLoadWeightType.Net]: null,
              [enumLoadWeightType.QuantityOwed]: removedItem.quantityOwedWeight,
            },
            includeInSalesOrder: true,
            pendingSalesOrderMaterialSelection: false,
          };
          lineItems.push(newItem);
          return { ...prevForm, lineItems };
        }

        const newItem: LoadFormLineItem = {
          type,
          id: newItemId,
          itemId: null,
          tagId: null,
          phase: phase ?? null,
          unitPrice: null,
          pricingUnit: null,
          weights: {
            [enumLoadWeightType.Gross]: null,
            [enumLoadWeightType.Tare]: null,
            [enumLoadWeightType.Net]: null,
            [enumLoadWeightType.QuantityOwed]: null,
          },
          includeInSalesOrder: true,
          pendingSalesOrderMaterialSelection: true,
        };

        lineItems.push(newItem);
        return { ...prevForm, lineItems };
      });
      setMaterialCellFocusSignal(newItemId);
    },
    [phase, setMaterialCellFocusSignal, updateForm],
  );

  const deleteItem = React.useCallback(
    (id: string) => {
      updateForm((prevForm) => {
        const lineItems = prevForm.lineItems.filter((line) => line.id !== id);
        return { ...prevForm, lineItems };
      });
    },
    [updateForm],
  );

  return {
    updateItem,
    addItem,
    addSalesOrderItem,
    deleteItem,
  };
};
